import {CommonModule} from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  NgModule,
  NO_ERRORS_SCHEMA,
} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {
  TuiTableCell,
  TuiTableDirective,
  TuiTableTbody,
  TuiTableTd,
  TuiTableTh,
} from '@taiga-ui/addon-table';
import {
  TUI_DIALOGS_CLOSE,
  TuiAppearance,
  TuiAutoColorPipe,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiHint,
  TuiInitialsPipe,
} from '@taiga-ui/core';
import {
  TuiAvatar,
  TuiBadge,
  TuiCheckbox,
  TuiChevron,
  TuiPagination,
  TuiProgressBar,
  TuiStatus,
} from '@taiga-ui/kit';

import {GateweeAccordionComponent} from './gatewee-accordion/gatewee-accordion.component';
import {GateweeCollapsibleDirective} from './gatewee-collapsible/gatewee-collapsible.directive';
import {GateweeExpandChevronComponent} from './gatewee-expand-chevron/gatewee-expand-chevron.component';
import {GateweeButtonComponent} from './gatewee-button/gatewee-button.component';
import {GateweeCheckboxModule} from './gatewee-checkbox/gatewee-checkbox.module';
import {GateweeContentPageComponent} from './gatewee-content-page/gatewee-content-page.component';
import {GateweeDatepickerRangeComponent} from './gatewee-datepicker-range/gatewee-datepicker-range.component';
import {GateweeDatepickerV2Component} from './gatewee-datepicker-v2/gatewee-datepicker-v2.component';
import {GateweeBankSvgLogoComponent} from './gatewee-bank-svg-logo/gatewee-bank-svg-logo.component';
import {GateweeIconComponent} from './gatewee-icon/gatewee-icon.component';
import {GateweeIntegrationsComponent} from './gatewee-integrations/gatewee-integrations.component';
import {GateweeWalletsComponent} from './gatewee-wallets/gatewee-wallets.component';
import {GateweeWalletsModule} from './gatewee-wallets/gatewee-wallets.module';
import {GateweePageSettingsComponent} from './gatewee-page-settings/gatewee-page-settings.component';
import {GateweeSettingsComponent} from './gatewee-settings/gatewee-settings.component';
import {GateweeSettingsModule} from './gatewee-settings/gatewee-settings.module';
import {GateweeApiKeysComponent} from './gatewee-api-keys/gatewee-api-keys.component';
import {GateweeTasksComponent} from './gatewee-tasks/gatewee-tasks.component';
import {GateweeNotificationsComponent} from './gatewee-notifications/gatewee-notifications.component';
import {GateweePixKeysComponent} from './gatewee-pix-keys/gatewee-pix-keys.component';
import {GateweeUsersComponent} from './gatewee-users/gatewee-users.component';
import {GateweeFeesSettingsComponent} from './gatewee-fees-settings/gatewee-fees-settings.component';
import {GateweeFeesSettingsV2Component} from './gatewee-fees-settings-v2/gatewee-fees-settings-v2.component';
import {GateweePaymentRulesComponent} from './gatewee-payment-rules/gatewee-payment-rules.component';
import {GateweeCheckoutPaymentMethodsComponent} from './gatewee-checkout-payment-methods/gatewee-checkout-payment-methods.component';
import {GateweeSelectV3Component} from './gatewee-select-v3/gatewee-select-v3.component';
import {GateweeSelectAsyncComponent} from './gatewee-select-async/gatewee-select-async.component';
import {GateweeSplitPaymentComponent} from './gatewee-split-payment/gatewee-split-payment.component';
import {GateweeStyleSettingsComponent} from './gatewee-style-settings/gatewee-style-settings.component';
import {GateweeStepperComponent} from './gatewee-stepper/gatewee-stepper.component';
import {GateweeMultiSelectComponent} from './gatewee-multi-select/gatewee-multi-select.component';

// Material Libs
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MaterialModule} from '@gatewee/material';
import {ComponentsV1Module} from '../v1/components-v1.module';
import {ErrorMessageComponent} from '../v1/error-component/error-message.component';
import {GateweeTableV2Component} from './gatewee-table-v2/gatewee-table-v2.component';
import {GateweeDocSidebarComponent} from './gatewee-doc-sidebar/gatewee-doc-sidebar.component';
import {GateweeHeaderComponent} from './gatewee-wrapper/gatewee-header/gatewee-header.component';
import {GateweeNavigationComponent} from './gatewee-wrapper/gatewee-navigation/gatewee-navigation.component';
import {GateweeWrapperComponent} from './gatewee-wrapper/gatewee-wrapper.component';
import {NameDocumentTableitemV2Component} from './name-document-tableitem-v2/name-document-tableitem-v2.component';
import {GateweeWithdrawalReceiverTableitemV2Component} from './gatewee-withdrawal-receiver-tableitem-v2/gatewee-withdrawal-receiver-tableitem-v2.component';
import {UtilsModule} from '@gatewee/helpers';
import {GateweeInputV2Component} from './gatewee-input-v2/gatewee-input-v2.component';
import {GateweeDocumentInputComponent} from './gatewee-document-input/gatewee-document-input.component';
import {GateweeDiscountComponent} from './gatewee-discount/gatewee-discount.component';
import {GateweeInterestFineComponent} from './gatewee-interest-fine/gatewee-interest-fine.component';
import {GateweeItemsComponent} from './gatewee-items/gatewee-items.component';
import {merge} from 'rxjs';
import {GateweeConfigSubscribe} from './gatewee-config-subscribe/gatewee-config-subscribe.component';
import {GateweeUploadComponent} from './gatewee-upload/gatewee-upload.component';
import {GateweeDrawerComponent} from './gatewee-drawer/gatewee-drawer.component';
import {GateweeFilterComponent} from './gatewee-filter/gatewee-filter.component';
import {GateweeRecipientStatOverviewComponent} from './gatewee-recipient-stat-overview/gatewee-recipient-stat-overview.component';
import {GateweeMetricComponent} from './gatewee-metric/gatewee-metric.component';
import {FeePlanBuilderComponent} from './fee-plan-builder/fee-plan-builder.component';
import {ProviderTransactionsModalComponent} from './provider-transactions-modal/provider-transactions-modal.component';
import {GateweeFeeSnapshotTableComponent} from './gatewee-fee-snapshot-table/gatewee-fee-snapshot-table.component';
import {GateweeTransactionsFeesTabsComponent} from './gatewee-transactions-fees-tabs/gatewee-transactions-fees-tabs.component';
import {GateweeWebhookMetadataComponent} from './gatewee-webhook-metadata/gatewee-webhook-metadata.component';
import {GateweeInvoiceStatusHistoryComponent} from './gatewee-invoice-status-history/gatewee-invoice-status-history.component';
import {GateweeWithdrawalStatusHistoryComponent} from './gatewee-withdrawal-status-history/gatewee-withdrawal-status-history.component';
import {GateweeStatusHistoryComponent} from './gatewee-status-history/gatewee-status-history.component';

import {GateweeInvoiceClientInfoComponent} from './gatewee-invoice-client-info/gatewee-invoice-client-info.component';
import {GateweeAmountFeeBreakdownComponent} from './gatewee-amount-fee-breakdown/gatewee-amount-fee-breakdown.component';
import {GateweePaymentReceiptComponent} from './gatewee-payment-receipt/gatewee-payment-receipt.component';
import {GateweePaymentReceiptExportCardComponent} from './gatewee-payment-receipt-export-card/gatewee-payment-receipt-export-card.component';
import {GateweePixAutomaticMandateComponent} from './gatewee-pix-automatic-mandate/gatewee-pix-automatic-mandate.component';
import {GateweeWithdrawalReceiverInfoComponent} from './gatewee-withdrawal-receiver-info/gatewee-withdrawal-receiver-info.component';
import {GateweeUserContextComponent} from './gatewee-user-context/gatewee-user-context.component';
import {GateweeProfileSecurityComponent} from './gatewee-profile-security/gatewee-profile-security.component';
import {GateweeProfileOverviewComponent} from './gatewee-profile-overview/gatewee-profile-overview.component';
import {GateweeProfileNotificationPreferencesComponent} from './gatewee-profile-notification-preferences/gatewee-profile-notification-preferences.component';
import {TwoFactorAuthComponent} from './two-factor-auth/two-factor-auth.component';
import {GateweeRadioComponent} from './gatewee-radio/gatewee-radio.component';
import {GateweePoolBalanceViewComponent} from './gatewee-pool-balance-view/gatewee-pool-balance-view.component';
import {GateweeBankAccountRegistrationComponent} from './gatewee-bank-account-registration/gatewee-bank-account-registration.component';
import {twoFactorAuthComponentToken} from '@gatewee/services/tokens/two-factor-auth-component.token';

const tuiDataListArray = [...TuiDataList];
const tuiDropdownArray = [...TuiDropdown];
const tuiHintArray = [...TuiHint];

const components = [
  GateweeContentPageComponent,
  GateweeAccordionComponent,
  GateweeSplitPaymentComponent,
  GateweePaymentRulesComponent,
  GateweeCheckoutPaymentMethodsComponent,
  GateweeTableV2Component,
  NameDocumentTableitemV2Component,
  GateweeWithdrawalReceiverTableitemV2Component,
  GateweeIntegrationsComponent,
  GateweeWalletsComponent,
  GateweeSettingsComponent,
  GateweeApiKeysComponent,
  GateweeTasksComponent,
  GateweeNotificationsComponent,
  GateweePixKeysComponent,
  GateweeUsersComponent,
  GateweeFeesSettingsComponent,
  GateweeFeesSettingsV2Component,
  GateweeDiscountComponent,
  GateweeInterestFineComponent,
  GateweeConfigSubscribe,
  GateweeItemsComponent,
  FeePlanBuilderComponent,
  ProviderTransactionsModalComponent,
  GateweeRadioComponent,
];

const imports = [
  CommonModule,
  RouterModule,
  GateweeCollapsibleDirective,
  GateweeExpandChevronComponent,
  MaterialModule,
  ComponentsV1Module,
  FormsModule,
  ReactiveFormsModule,
  TranslateModule,
  GateweeIconComponent,
  GateweeCheckboxModule,
  GateweeBankSvgLogoComponent,
  GateweeButtonComponent,
  GateweeDatepickerV2Component,
  GateweeDatepickerRangeComponent,
  GateweeSelectV3Component,
  GateweeSelectAsyncComponent,
  ErrorMessageComponent,
  GateweeWrapperComponent,
  GateweeHeaderComponent,
  GateweeNavigationComponent,
  GateweeDocSidebarComponent,
  GateweeStyleSettingsComponent,
  GateweePageSettingsComponent,
  GateweeStepperComponent,
  TuiButton,
  ...tuiHintArray,
  TuiPagination,
  TuiProgressBar,
  TuiAvatar,
  TuiBadge,
  TuiStatus,
  TuiCheckbox,
  TuiChevron,
  TuiAppearance,
  ...tuiDataListArray,
  ...tuiDropdownArray,
  TuiTableDirective,
  TuiTableTh,
  TuiTableTd,
  TuiTableTbody,
  TuiTableCell,
  TuiInitialsPipe,
  TuiAutoColorPipe,
  UtilsModule,
  GateweeInputV2Component,
  GateweeDocumentInputComponent,
  GateweeSettingsModule,
  GateweeWalletsModule,
  GateweeUploadComponent,
  GateweeDrawerComponent,
  GateweeFilterComponent,
  GateweeMetricComponent,
  GateweeRecipientStatOverviewComponent,
  GateweeMultiSelectComponent,
  GateweeFeeSnapshotTableComponent,
  GateweeTransactionsFeesTabsComponent,
  GateweeWebhookMetadataComponent,
  GateweeInvoiceStatusHistoryComponent,
  GateweeWithdrawalStatusHistoryComponent,
  GateweeStatusHistoryComponent,
  GateweeInvoiceClientInfoComponent,
  GateweeAmountFeeBreakdownComponent,
  GateweePaymentReceiptComponent,
  GateweePaymentReceiptExportCardComponent,
  GateweePixAutomaticMandateComponent,
  GateweeWithdrawalReceiverInfoComponent,
  GateweeUserContextComponent,
  TwoFactorAuthComponent,
  GateweeProfileSecurityComponent,
  GateweeProfileOverviewComponent,
  GateweeProfileNotificationPreferencesComponent,
  GateweePoolBalanceViewComponent,
  GateweeBankAccountRegistrationComponent,
];

@NgModule({
  imports: [...imports],
  declarations: [...components],
  exports: [
    ...components,
    GateweeContentPageComponent,
    GateweeBankSvgLogoComponent,
    GateweeIconComponent,
    GateweeCheckboxModule,
    GateweeButtonComponent,
    GateweeDatepickerV2Component,
    GateweeDatepickerRangeComponent,
    GateweeSelectV3Component,
    GateweeSelectAsyncComponent,
    ErrorMessageComponent,
    GateweeWrapperComponent,
    GateweeHeaderComponent,
    GateweeNavigationComponent,
    GateweeDocSidebarComponent,
    GateweeTableV2Component,
    NameDocumentTableitemV2Component,
    GateweeWithdrawalReceiverTableitemV2Component,
    GateweeIntegrationsComponent,
    GateweeWalletsComponent,
    GateweeWalletsModule,
    GateweeSettingsComponent,
    GateweeStyleSettingsComponent,
    GateweePageSettingsComponent,
    GateweeApiKeysComponent,
    GateweeTasksComponent,
    GateweeNotificationsComponent,
    GateweePixKeysComponent,
    GateweeUsersComponent,
    GateweeFeesSettingsComponent,
    GateweeFeesSettingsV2Component,
    GateweeInputV2Component,
    GateweeDocumentInputComponent,
    GateweeStepperComponent,
    GateweeFilterComponent,
    GateweeMetricComponent,
    GateweeRecipientStatOverviewComponent,
    GateweeMultiSelectComponent,
    FeePlanBuilderComponent,
    GateweeFeeSnapshotTableComponent,
    GateweeTransactionsFeesTabsComponent,
    GateweeWebhookMetadataComponent,
    GateweeInvoiceStatusHistoryComponent,
    GateweeWithdrawalStatusHistoryComponent,
    GateweeStatusHistoryComponent,
    GateweeInvoiceClientInfoComponent,
    GateweeAmountFeeBreakdownComponent,
    GateweePaymentReceiptComponent,
    GateweePaymentReceiptExportCardComponent,
    GateweePixAutomaticMandateComponent,
    GateweeWithdrawalReceiverInfoComponent,
    GateweeUserContextComponent,
    GateweeDrawerComponent,
    TwoFactorAuthComponent,
    GateweeProfileSecurityComponent,
    GateweeProfileOverviewComponent,
    GateweeProfileNotificationPreferencesComponent,
    GateweeCollapsibleDirective,
    GateweeExpandChevronComponent,
    GateweePoolBalanceViewComponent,
    GateweeBankAccountRegistrationComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  providers: [
    // This has to be added to global providers, shown here for demonstration purposes only
    {
      provide: TUI_DIALOGS_CLOSE,
      useFactory: () => merge(inject(Router).events),
    },
    {
      provide: twoFactorAuthComponentToken,
      useValue: TwoFactorAuthComponent,
    },
  ],
})
export class ComponentsV2Module {}
